/**
 * Organization admins list hook using React Relay.
 *
 * Server-side search (nested admin first/last/email ilike) and cursor
 * pagination over listOrganizationAdmins. The Joined column comes from the
 * membership row's createdOn.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import CustomToast from '../../../../components/Redesing/custom-toast';
import type {
  useOrganizationAdminsRelayDirectQuery as UseOrganizationAdminsRelayDirectQuery,
  useOrganizationAdminsRelayDirectQuery$variables,
  OrganizationAdminFilterInput,
  OrganizationAdminSortInput,
} from './__generated__/useOrganizationAdminsRelayDirectQuery.graphql';

const OrganizationAdminsListDirectQuery = graphql`
  query useOrganizationAdminsRelayDirectQuery(
    $first: Int
    $after: String
    $filter: OrganizationAdminFilterInput
    $sort: [OrganizationAdminSortInput!]
  ) {
    listOrganizationAdmins(first: $first, after: $after, filter: $filter, sort: $sort) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          createdOn
          admin {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

export type { OrganizationAdminFilterInput, OrganizationAdminSortInput };

export interface OrganizationAdminGraphQL {
  id: string;
  createdOn: string;
  admin: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
}

/**
 * Simplified filter options for common use cases
 */
export interface OrganizationAdminQueryFilter {
  organizationId?: string;
  search?: string;
}

function buildRelayFilter(
  filter?: OrganizationAdminQueryFilter
): OrganizationAdminFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: OrganizationAdminFilterInput = {
    removedOn: { isNil: true },
  };

  if (filter.organizationId) {
    relayFilter.organizationId = { eq: filter.organizationId };
  }
  if (filter.search) {
    const pattern = `%${filter.search}%`;
    // the API rejects `or` nested inside the admin relationship filter, so
    // each OR branch repeats the admin relationship at the top level
    relayFilter.or = [
      { admin: { firstName: { ilike: pattern } } },
      { admin: { lastName: { ilike: pattern } } },
      { admin: { email: { ilike: pattern } } },
    ];
  }

  return relayFilter;
}

/**
 * Hook that provides React Query-like interface for the organization admins list.
 *
 * Does NOT use Suspense - provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, count, hasMore, endCursor, isLoading } = useOrganizationAdminsRelayQuery(
 *   { organizationId: orgId, search: 'jane' },
 *   { limit: 25, after: afterCursor }
 * );
 */
export function useOrganizationAdminsRelayQuery(
  filter?: OrganizationAdminQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: OrganizationAdminSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<OrganizationAdminGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useOrganizationAdminsRelayDirectQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
        };

        const result = await fetchQuery<UseOrganizationAdminsRelayDirectQuery>(
          relayEnvironment,
          OrganizationAdminsListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listOrganizationAdmins?.edges
              ?.map((edge) => edge?.node)
              .filter(Boolean) ?? [];
          setData(nodes as OrganizationAdminGraphQL[]);
          setCount(result.listOrganizationAdmins?.count ?? undefined);
          setHasMore(result.listOrganizationAdmins?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listOrganizationAdmins?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch organization admins')
        );
        CustomToast({
          type: 'error',
          message: 'There was an error trying to fetch organization admins.',
        });
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
